/**
 * Semantic-similarity fallback for the HFCL Chat Bot.
 *
 * The static token + Levenshtein matcher in `hfclChatHints.js` handles typos
 * and direct keyword usage. This file handles the harder cases — when the
 * user paraphrases without sharing any keyword (e.g. "I want to invite a
 * teammate" should still map to the Add User intent).
 *
 * Implementation notes:
 *   • Transformers.js is loaded from the public CDN at RUNTIME using a
 *     dynamic ESM import with the `webpackIgnore` magic comment. This
 *     means it is NOT bundled by webpack, so adding / removing the
 *     semantic feature never touches the regular dev build.
 *   • The all-MiniLM-L6-v2 sentence-embedding model (~25 MB) is fetched
 *     from the Hugging Face CDN on first use and then cached by the
 *     browser. Subsequent loads are near-instant.
 *   • If the model or the library fails to load (offline, CSP, anything
 *     else), every helper returns `null` and the chat continues to work
 *     using only the static matcher.
 */

// Pin to a known-good version. Bump intentionally when you want a new model
// runtime; we never auto-upgrade.
const TRANSFORMERS_URL =
	'https://cdn.jsdelivr.net/npm/@xenova/[email protected]/+esm';
const MODEL_ID = 'Xenova/all-MiniLM-L6-v2';
const SIMILARITY_THRESHOLD = 0.55;

let embedderPromise = null;
let anchorsPromise = null;

/**
 * Lazy-load Transformers.js from the CDN (bypassing webpack), then boot the
 * feature-extraction pipeline. Subsequent callers share the same in-flight
 * promise.
 *
 * @return {Promise<Function>} the feature-extraction pipeline
 */
const getEmbedder = () => {
	if (embedderPromise) return embedderPromise;
	embedderPromise = (async () => {
		// Webpack: do NOT bundle this URL — it is a runtime ESM import.
		// eslint-disable-next-line no-inline-comments
		const transformers = await import(/* webpackIgnore: true */ TRANSFORMERS_URL);
		// Use the Hugging Face CDN cache — no models bundled with the app.
		if (transformers.env) {
			transformers.env.allowLocalModels = false;
		}
		return transformers.pipeline('feature-extraction', MODEL_ID);
	})().catch((err) => {
		console.error('[hfcl-chat] failed to load semantic model:', err);
		embedderPromise = null; // allow retry next time
		throw err;
	});
	return embedderPromise;
};

/**
 * Convert one phrase into a normalised 384-dim Float32Array.
 *
 * @param {string} text
 * @return {Promise<Float32Array>}
 */
const embed = async (text) => {
	const extractor = await getEmbedder();
	const out = await extractor(text, { pooling: 'mean', normalize: true });
	return out.data;
};

/**
 * Cosine similarity for two normalised vectors. Equivalent to dot product
 * because the embedder returns unit-length vectors.
 *
 * @param {Float32Array} a
 * @param {Float32Array} b
 * @return {number}
 */
const cosine = (a, b) => {
	let dot = 0;
	for (let i = 0; i < a.length; i += 1) dot += a[i] * b[i];
	return dot;
};

/**
 * Build (and cache) the keyword vector index for the given hints.
 *
 * @param {Array<Object>} hints
 * @return {Promise<Array<{hint: Object, keyword: string, vector: Float32Array}>>}
 */
const getAnchors = (hints) => {
	if (anchorsPromise) return anchorsPromise;
	anchorsPromise = (async () => {
		const rows = [];
		for (const hint of hints) {
			// Embed each keyword phrase as a separate anchor.
			for (const keyword of hint.keywords) {
				/* eslint-disable no-await-in-loop */
				const vector = await embed(keyword);
				rows.push({ hint, keyword, vector });
				/* eslint-enable no-await-in-loop */
			}
			// Also embed the human-readable label and description so the matcher
			// has more semantic surface area to work with.
			if (hint.label) {
				/* eslint-disable no-await-in-loop */
				rows.push({ hint, keyword: hint.label, vector: await embed(hint.label) });
				/* eslint-enable no-await-in-loop */
			}
			if (hint.description) {
				/* eslint-disable no-await-in-loop */
				rows.push({ hint, keyword: hint.description, vector: await embed(hint.description) });
				/* eslint-enable no-await-in-loop */
			}
		}
		return rows;
	})().catch((err) => {
		console.error('[hfcl-chat] failed to build anchor index:', err);
		anchorsPromise = null;
		throw err;
	});
	return anchorsPromise;
};

/**
 * Returns true once the semantic model has finished loading and indexing.
 *
 * @return {boolean}
 */
export const isSemanticReady = () =>
	Boolean(embedderPromise && anchorsPromise);

/**
 * Eagerly warm the model + anchors in the background. Call this from a
 * place that runs after first paint, e.g. when the chat panel opens.
 *
 * @param {Array<Object>} hints
 * @return {void}
 */
export const warmupSemantic = (hints) => {
	// Fire and forget — errors are already logged by the helpers.
	getAnchors(hints).catch(() => {});
};

/**
 * Find the hint whose embedded keyword is most similar to the user message.
 *
 * @param {string} query - the user message
 * @param {Array<Object>} hints - the registry from hfclChatHints
 * @param {Object}   [opts]
 * @param {number}   [opts.threshold=SIMILARITY_THRESHOLD] - cosine threshold
 * @return {Promise<{hint: Object, score: number, anchor: string} | null>}
 */
export const findSemanticHint = async (query, hints, opts = {}) => {
	const threshold = typeof opts.threshold === 'number' ? opts.threshold : SIMILARITY_THRESHOLD;
	if (!query || !hints || hints.length === 0) return null;

	try {
		const [queryVec, anchors] = await Promise.all([
			embed(query),
			getAnchors(hints),
		]);
		let best = null;
		let bestScore = -Infinity;
		for (const row of anchors) {
			const score = cosine(queryVec, row.vector);
			if (score > bestScore) {
				bestScore = score;
				best = row;
			}
		}
		if (!best || bestScore < threshold) return null;
		return { hint: best.hint, score: bestScore, anchor: best.keyword };
	} catch (err) {
		// Model failed to load or query failed — fall through gracefully.
		return null;
	}
};

export const HFCL_SEMANTIC_THRESHOLD = SIMILARITY_THRESHOLD;
